// PRPs/prds/classificacao-desempate.prd.md AC-9 fonte única via GraphQL
// Phase 3 — Por-atleta + consumo no front
// Covers plan AC-A4: encapsula query classificacaoPorGrupo em services/
// Mirrors: inplay-front/src/services/faseGrupoPartidaEquipeService.ts
import { gql, ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { apolloClient } from '../apollo/client';

const baseErrorMessage = 'Não foi possível completar a operação';

function normalizeGraphqlError(error: unknown): never {
  if (error instanceof ApolloError) {
    const backendMessage = error.graphQLErrors?.[0]?.message ?? error.message ?? 'erro desconhecido.';
    throw new Error(`${baseErrorMessage}: ${backendMessage}`);
  }

  if (error instanceof GraphQLError) {
    throw new Error(`${baseErrorMessage}: ${error.message ?? 'erro desconhecido.'}`);
  }

  if (error instanceof Error) {
    throw error;
  }

  throw new Error(`${baseErrorMessage}: erro desconhecido.`);
}

/**
 * Documento GQL para a query classificacaoPorGrupo.
 * Seleciona todos os campos retornados pelo MotorClassificacao do backend.
 */
export const CLASSIFICACAO_POR_GRUPO = gql`
  query ClassificacaoPorGrupo($faseGrupoPartidasId: ID!) {
    classificacaoPorGrupo(faseGrupoPartidasId: $faseGrupoPartidasId) {
      posicao
      equipeId
      nomeCompetidor
      vitorias
      derrotas
      setsPro
      setsContra
      gamesPro
      gamesContra
      saldoSets
      saldoGames
    }
  }
`;

/**
 * DTO de saída do backend mapeado para o frontend.
 * Espelha os campos de LinhaClassificacao retornados pela query.
 */
export interface LinhaClassificacaoFront {
  posicao: number;
  equipeId: string;
  nomeCompetidor?: string | null;
  vitorias: number;
  derrotas: number;
  setsPro: number;
  setsContra: number;
  gamesPro: number;
  gamesContra: number;
  saldoSets: number;
  saldoGames: number;
}

type ClassificacaoPorGrupoData = {
  classificacaoPorGrupo: LinhaClassificacaoFront[] | null | undefined;
};

/**
 * Documento GQL para a query classificacaoPorCategoria (ranking por atleta).
 * Mesma seleção de campos de CLASSIFICACAO_POR_GRUPO (mirror), consolidada
 * por categoria (multi-grupo) — usada pelo ranking individual (rodízio).
 */
export const CLASSIFICACAO_POR_CATEGORIA = gql`
  query ClassificacaoPorCategoria($categoriaTorneioId: ID!) {
    classificacaoPorCategoria(categoriaTorneioId: $categoriaTorneioId) {
      posicao
      equipeId
      nomeCompetidor
      vitorias
      derrotas
      setsPro
      setsContra
      gamesPro
      gamesContra
      saldoSets
      saldoGames
    }
  }
`;

type ClassificacaoPorCategoriaData = {
  classificacaoPorCategoria: LinhaClassificacaoFront[] | null | undefined;
};

/**
 * Busca a classificação de um grupo via query GraphQL.
 * Usa fetchPolicy 'network-only' para sempre obter dados frescos do backend.
 * Retorna a lista já ordenada pelo MotorClassificacao do backend.
 *
 * @param faseGrupoPartidasId - ID do grupo (FaseGrupoPartidas)
 * @returns Lista ordenada de LinhaClassificacaoFront, ou [] se sem dados
 */
export async function buscarClassificacaoPorGrupo(
  faseGrupoPartidasId: string,
): Promise<LinhaClassificacaoFront[]> {
  try {
    const { data } = await apolloClient.query<ClassificacaoPorGrupoData>({
      query: CLASSIFICACAO_POR_GRUPO,
      variables: { faseGrupoPartidasId },
      fetchPolicy: 'network-only',
    });

    return (data?.classificacaoPorGrupo ?? []).map((linha) => ({
      ...linha,
      nomeCompetidor: linha.nomeCompetidor ?? null,
    }));
  } catch (error) {
    normalizeGraphqlError(error);
  }
}

/**
 * Busca a classificação por categoria (ranking por atleta, multi-grupo
 * consolidado) via query GraphQL. Usa fetchPolicy 'network-only' para
 * sempre obter dados frescos do backend. Mirrors buscarClassificacaoPorGrupo.
 *
 * @param categoriaTorneioId - ID da CategoriaTorneio
 * @returns Lista ordenada de LinhaClassificacaoFront, ou [] se sem dados
 */
export async function buscarClassificacaoPorCategoria(
  categoriaTorneioId: string,
): Promise<LinhaClassificacaoFront[]> {
  try {
    const { data } = await apolloClient.query<ClassificacaoPorCategoriaData>({
      query: CLASSIFICACAO_POR_CATEGORIA,
      variables: { categoriaTorneioId },
      fetchPolicy: 'network-only',
    });

    return (data?.classificacaoPorCategoria ?? []).map((linha) => ({
      ...linha,
      nomeCompetidor: linha.nomeCompetidor ?? null,
    }));
  } catch (error) {
    normalizeGraphqlError(error);
  }
}
